/* eslint-disable max-statements */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core'
import { UntypedFormControl } from '@angular/forms'
import { PortfolioHoldingResponse } from 'src/app/models/portfolio-models/portfolio-holding-response.model'

import { PortfolioService } from '../../../portfolio.service'

const PAGE_SIZE = 10
const TOTAL_HOLDINGS_THRESHOLD = 500
const SHORT_TERM_RESERVE = 'short-term reserve'
const PCF_HOLDINGS = 'pcf-holdings'

@Component({
  selector: 'holding-details-pagination',
  templateUrl: './holding-details-pagination.component.html',
  styleUrls: ['./holding-details-pagination.component.scss'],
})
export class HoldingDetailsPaginationComponent {
  @Input() listSize: number
  @Input() currentType: string
  @Input() fundId: string
  @Input() viewDaily: boolean
  @Input() isMoneyMarket: boolean

  @Output() newPageNumber = new EventEmitter<number>()
  @Output() extraHoldings = new EventEmitter<
    Record<string, number | PortfolioHoldingResponse>
  >()
  @ViewChild('selectElement') selectElement: ElementRef<HTMLElement>

  public page: number
  public pageSelect: UntypedFormControl = new UntypedFormControl(1)
  public PAGE_SIZE: number
  public totalHoldings: number
  public extraHoldingsSet = false
  public alreadySetExtraHoldings = []

  constructor(private readonly portfolioService: PortfolioService) {}

  // eslint-disable-next-line complexity, max-statements
  changePage(newPage: number) {
    if (newPage * PAGE_SIZE < this.listSize + PAGE_SIZE && newPage >= 1) {
      let fireFetch = false
      this.page = newPage
      this.pageSelect.setValue(this.page)
      this.totalHoldings =
        this.page * PAGE_SIZE < this.listSize
          ? this.page * PAGE_SIZE
          : this.listSize
      const hasBeenSet = this.determineTypeHasBeenSet()
      if (this.totalHoldings > TOTAL_HOLDINGS_THRESHOLD && !hasBeenSet) {
        fireFetch = true
        this.extraHoldingsSet = true
      }
      if (fireFetch === true && this.currentType !== PCF_HOLDINGS) {
        this.getExtraHoldingsData()
      } else {
        this.newPageNumber.emit(this.page)
      }
    }
  }
  // eslint-disable-next-line complexity
  getExtraHoldingsData() {
    let holdingsPath = this.currentType

    if (this.currentType === 'equity') {
      holdingsPath = 'stock'
    } else if (this.currentType === SHORT_TERM_RESERVE && this.isMoneyMarket) {
      holdingsPath = 'money-market'
    } else if (this.currentType === SHORT_TERM_RESERVE) {
      holdingsPath = 'short-term-reserve'
    } else if (this.currentType === PCF_HOLDINGS) {
      holdingsPath = 'pcf'
    }
    this.portfolioService
      .getExtraHoldingsData(
        this.fundId,
        holdingsPath,
        this.viewDaily ? 'daily' : '',
      )
      .subscribe((extraHoldingsData) => {
        this.pushHoldingType()
        this.extraHoldings.emit({
          holdings: extraHoldingsData,
          page: this.page,
        })
      })
  }
  // eslint-disable-next-line complexity
  pushHoldingType() {
    let finalType = ''
    switch (true) {
      case this.isMoneyMarket && this.currentType === SHORT_TERM_RESERVE:
        finalType = 'mm-holdings'
        break
      case this.currentType === SHORT_TERM_RESERVE && this.viewDaily:
        finalType = 'str-holdings-daily'
        break
      case this.currentType === SHORT_TERM_RESERVE:
        finalType = 'str-holdings'
        break
      case this.currentType === 'bond' && this.viewDaily:
        finalType = 'bond-holdings-daily'
        break
      case this.currentType === 'bond':
        finalType = 'bond-holdings'
        break
      case this.currentType === 'equity' && this.viewDaily:
        finalType = 'equity-holdings-daily'
        break
      case this.currentType === 'equity':
        finalType = 'equity-holdings'
        break
      case this.currentType === 'derivative' && this.viewDaily:
        finalType = 'derivative-holdings-daily'
        break
      case this.currentType === 'derivative':
        finalType = 'derivative-holdings'
        break
      case this.currentType === PCF_HOLDINGS:
        finalType = PCF_HOLDINGS
        break
      case this.currentType === 'currency':
        finalType = 'currency-holdings'
        break
      case this.currentType === 'commodity':
        finalType = 'commodity-holdings'
        break
    }
    this.alreadySetExtraHoldings.push(finalType)
  }

  /* eslint-disable complexity */
  determineTypeHasBeenSet(): boolean {
    const type = this.alreadySetExtraHoldings
    const hasAlreadyBeenSet = false
    if (
      this.determineShortTermReserve(type) ||
      this.determineEquity(type) ||
      this.determineBond(type) ||
      this.determineDerivative(type) ||
      (this.currentType === PCF_HOLDINGS && type.includes(PCF_HOLDINGS)) ||
      (this.currentType === 'currency' && type.includes('currency-holdings')) ||
      (this.currentType === 'commodity' && type.includes('commodity-holdings'))
    ) {
      return true
    }
    return hasAlreadyBeenSet
  }

  determineShortTermReserve(type) {
    if (
      this.currentType === SHORT_TERM_RESERVE &&
      ((this.isMoneyMarket && type.includes('mm-holdings')) ||
        (this.viewDaily && type.includes('str-holdings-daily')) ||
        type.includes('str-holdings'))
    ) {
      return true
    }
    return false
  }

  determineEquity(type) {
    if (
      this.currentType === 'equity' &&
      ((this.viewDaily && type.includes('equity-holdings-daily')) ||
        type.includes('equity-holdings'))
    ) {
      return true
    }
    return false
  }

  determineBond(type) {
    if (
      this.currentType === 'bond' &&
      ((this.viewDaily && type.includes('bond-holdings-daily')) ||
        type.includes('bond-holdings'))
    ) {
      return true
    }
    return false
  }

  determineDerivative(type) {
    if (
      this.currentType === 'derivative' &&
      ((this.viewDaily && type.includes('derivative-holdings-daily')) ||
        type.includes('derivative-holdings'))
    ) {
      return true
    }
    return false
  }
}
